import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { UNKNOWN_USER } from "constants/proptypes";
import { useLoadSessionQuery } from "../features/mc-square-api/mc-square-api-slice";

const POLLING_REQUEST_FILTER_INTERVAL = 30;

const THROTTLED_PATHS = ["/syncs/latest", "/overview_stat"];

const FILTERED_DOMAINS = ["www.google-analytics.com", "google-analytics.com"];

const ErrorBoundaryContainer = ({
  children,
  bugsnagApiKey = null,
  bugsnagReleaseStage,
  revision = null,
}) => {
  const { data: squareUser = UNKNOWN_USER } = useLoadSessionQuery();
  const requestCounters = useRef({});

  if (!bugsnagApiKey || !revision) return children;

  const handleThrottledRequest = (breadcrumb, path) => {
    if (!requestCounters.current[path]) {
      requestCounters.current[path] = 0;
    }

    const currentCount = requestCounters.current[path];
    requestCounters.current[path] = currentCount + 1;

    if (currentCount % POLLING_REQUEST_FILTER_INTERVAL === 1) {
      // eslint-disable-next-line no-param-reassign
      breadcrumb.metadata.pollingRequestCount = currentCount;
      return true;
    }

    console.debug(
      `Bugsnag breadcrumb skipped for ${path} (${currentCount % POLLING_REQUEST_FILTER_INTERVAL})`
    );
    return false;
  };

  useMemo(() => {
    Bugsnag.start({
      apiKey: bugsnagApiKey,
      appType: "client",
      appVersion: revision,
      plugins: [new BugsnagPluginReact()],
      releaseStage: bugsnagReleaseStage,
      maxBreadcrumbs: 50,
      onBreadcrumb: (breadcrumb) => {
        // eslint-disable-next-line no-param-reassign
        breadcrumb.metadata = {
          ...breadcrumb.metadata,
          currentUrl: window.location.href,
        };

        // Don't record a breadcrumb for our logs about skipping recording breadcrumbs.
        if (
          breadcrumb.type === "log" &&
          breadcrumb.metadata["[0]"].includes("Bugsnag breadcrumb skipped")
        ) {
          return false;
        }

        if (breadcrumb.type === "request" && breadcrumb.metadata?.url) {
          const { url } = breadcrumb.metadata;

          // Filter out Google Analytics requests completely
          const isFilteredDomain = FILTERED_DOMAINS.some((domain) =>
            url.includes(domain)
          );
          if (isFilteredDomain) {
            console.debug(
              `Bugsnag breadcrumb skipped for ${new URL(url).hostname}`
            );
            return false;
          }

          // Throttle polling requests
          const throttledPath = THROTTLED_PATHS.find((path) =>
            url.includes(path)
          );
          if (throttledPath) {
            return handleThrottledRequest(breadcrumb, throttledPath);
          }
        }

        return true;
      },
    });
  }, []);

  useMemo(() => {
    Bugsnag.addMetadata("squareUser", squareUser);
  }, [squareUser]);

  const Boundary = useMemo(
    () => Bugsnag.getPlugin("react").createErrorBoundary(React),
    []
  );

  return <Boundary>{children}</Boundary>;
};

ErrorBoundaryContainer.propTypes = {
  children: PropTypes.node.isRequired,
  bugsnagApiKey: PropTypes.string,
  bugsnagReleaseStage: PropTypes.string.isRequired,
  revision: PropTypes.string,
};

export default ErrorBoundaryContainer;
